<!-- 宠物统计：领取率、宠物分布、等级分布、当日状态分布与班级明细。最后更新：2026-10-07 -->
<template>
  <section class="dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick" :type="0"></DepartmentPane>
    </section>
    <section class="dp-right pet-stat-page">
      <div class="page-head">
        <p class="dp-dept-hint">
          <span class="dp-dept-hint__label">统计范围</span>
          <span class="dp-dept-hint__value">{{ departmentName || '未选择' }}</span>
        </p>
        <div class="filter-bar">
          <el-select v-model="query.semesterId" size="small" placeholder="学期" class="filter-bar__semester" @change="handleSemesterChange">
            <el-option v-for="s in semesters" :key="s.semesterId" :value="s.semesterId" :label="s.semesterName + (s.isCurrent ? '（当前）' : '')" />
          </el-select>
          <el-date-picker
            v-model="query.viewDate"
            type="date"
            size="small"
            value-format="yyyy-MM-dd"
            placeholder="统计日期"
            :clearable="false"
            :picker-options="datePickerOptions"
            class="filter-bar__date"
            @change="load" />
          <el-button type="primary" size="small" icon="el-icon-refresh" :loading="loading" @click="load">刷新</el-button>
        </div>
      </div>

      <div class="page-body" v-loading="loading">
        <div class="stat-strip">
          <div class="stat-card is-clickable" @click="openStudents('在读学生', {})">
            <span class="stat-card__label">在读学生<i class="el-icon-arrow-right"></i></span>
            <span class="stat-card__value">{{ stat.totalStudentCount || 0 }}</span>
          </div>
          <div class="stat-card stat-card--success is-clickable" @click="openStudents('已领取学生', { claimStatus: 1 }, ['已领取'])">
            <span class="stat-card__label">已领取 / 领取率<i class="el-icon-arrow-right"></i></span>
            <span class="stat-card__value">{{ stat.claimedCount || 0 }}<small>人 · {{ claimRate }}%</small></span>
          </div>
          <div class="stat-card stat-card--violet is-clickable" @click="openStudents('学期分明细', {})">
            <span class="stat-card__label">平均学期分<i class="el-icon-arrow-right"></i></span>
            <span class="stat-card__value">{{ stat.avgSemesterScore || 0 }}<small>分</small></span>
          </div>
          <div class="stat-card stat-card--warning is-clickable" @click="openStudents(dayText + '有评价学生', { activeOnly: true }, [dayText + '有评价'])">
            <span class="stat-card__label">{{ dayText }}有评价学生<i class="el-icon-arrow-right"></i></span>
            <span class="stat-card__value">{{ stat.activeTodayCount || 0 }}<small>人</small></span>
          </div>
        </div>

        <div class="panel-grid">
          <div class="panel">
            <div class="panel__head">
              <span class="panel__title">等级分布</span>
              <span class="panel__desc">{{ stat.semesterName || '—' }} · 截至 {{ query.viewDate }}，深色为已领取宠物</span>
            </div>
            <div v-if="levelList.length" class="bar-list">
              <div
                v-for="item in levelList"
                :key="item.levelIndex"
                class="bar-row is-clickable"
                @click="openStudents(`Lv.${item.levelIndex} ${item.levelName} 学生`, { levelIndex: item.levelIndex }, [`Lv.${item.levelIndex} ${item.levelName}`])">
                <span class="bar-row__label">
                  <span class="level-badge">Lv.{{ item.levelIndex }}</span>
                  <span class="bar-row__sub" :title="item.levelName">{{ item.levelName }}</span>
                </span>
                <div class="bar-track">
                  <span class="bar-fill bar-fill--soft" :style="{ width: barWidth(item.count, maxLevelCount) }"></span>
                  <span class="bar-fill" :style="{ width: barWidth(item.claimedCount, maxLevelCount) }"></span>
                </div>
                <span class="bar-row__value">{{ item.count }}<small v-if="item.count"> / {{ item.claimedCount }}</small></span>
              </div>
            </div>
            <div v-else class="panel-empty">暂无数据</div>
          </div>

          <div class="panel">
            <div class="panel__head">
              <span class="panel__title">{{ isToday ? '今日' : '当日' }}状态</span>
              <span class="panel__desc">按 {{ query.viewDate }} 的净分：大于 0 活力，等于 0 平稳，小于 0 调整</span>
            </div>
            <div class="mood-stack" v-if="stat.totalStudentCount">
              <span
                v-for="m in moodList"
                :key="m.mood"
                :class="['mood-stack__seg', 'mood-' + m.mood.toLowerCase()]"
                :style="{ width: barWidth(m.count, stat.totalStudentCount) }"
                :title="`${m.moodText} ${m.count} 人`"
                @click="openMood(m)"></span>
            </div>
            <div class="mood-legend">
              <div v-for="m in moodList" :key="m.mood" class="mood-legend__item is-clickable" @click="openMood(m)">
                <i :class="'mood-' + m.mood.toLowerCase()"></i>
                <span class="mood-legend__text">{{ m.moodText }}</span>
                <span class="mood-legend__value">{{ m.count }}<small>人 · {{ percent(m.count, stat.totalStudentCount) }}%</small></span>
              </div>
            </div>

            <div class="panel__head panel__head--sub">
              <span class="panel__title">宠物受欢迎度</span>
              <span class="panel__desc">当前领取人数</span>
            </div>
            <div v-if="petList.length" class="bar-list">
              <div
                v-for="pet in petList"
                :key="pet.petId"
                class="bar-row is-clickable"
                @click="openStudents(`领取「${pet.petName}」的学生`, { claimStatus: 1, petId: pet.petId }, [pet.petName])">
                <span class="bar-row__label">
                  <span class="bar-row__sub strong" :title="pet.petName">{{ pet.petName }}</span>
                  <span v-if="pet.status !== 1" class="off-tag">下架</span>
                </span>
                <div class="bar-track"><span class="bar-fill bar-fill--amber" :style="{ width: barWidth(pet.count, maxPetCount) }"></span></div>
                <span class="bar-row__value">{{ pet.count }}</span>
              </div>
            </div>
            <div v-else class="panel-empty">还没有宠物</div>
          </div>
        </div>

        <div class="panel">
          <div class="panel__head">
            <span class="panel__title">班级明细</span>
            <span class="panel__desc">共 {{ classList.length }} 个班级</span>
          </div>
          <el-table :data="pagedClassList" size="small" class="class-table" row-class-name="is-clickable" @row-click="openClass">
            <el-table-column prop="departmentName" label="班级" min-width="140" align="center" show-overflow-tooltip />
            <el-table-column prop="studentCount" label="学生数" min-width="90" align="center" />
            <el-table-column prop="claimRate" label="领取率" min-width="220" align="center">
              <template slot-scope="{ row }">
                <div class="rate-cell">
                  <div class="bar-track bar-track--thin"><span class="bar-fill bar-fill--green" :style="{ width: row.claimRate + '%' }"></span></div>
                  <span class="rate-cell__text">{{ row.claimedCount }}/{{ row.studentCount }} · {{ row.claimRate }}%</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="avgSemesterScore" label="平均学期分" min-width="120" align="center" />
            <el-table-column prop="avgLevel" label="平均等级" min-width="100" align="center">
              <template slot-scope="{ row }">Lv.{{ row.avgLevel }}</template>
            </el-table-column>
            <el-table-column prop="energeticCount" :label="(isToday ? '今日' : '当日') + '活力'" min-width="100" align="center">
              <template slot-scope="{ row }"><span class="mood-num mood-energetic-text">{{ row.energeticCount }}</span></template>
            </el-table-column>
            <el-table-column prop="adjustingCount" :label="(isToday ? '今日' : '当日') + '调整'" min-width="100" align="center">
              <template slot-scope="{ row }"><span class="mood-num mood-adjusting-text">{{ row.adjustingCount }}</span></template>
            </el-table-column>
          </el-table>
          <div v-if="classList.length" class="class-pager">
            <span class="class-pager__hint"><i class="el-icon-info"></i>点击班级行可查看学生明细</span>
            <el-pagination
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="classList.length"
              :current-page.sync="classPage"
              :page-size.sync="classPageSize"
              :page-sizes="classPageSizes"
              @size-change="classPage = 1" />
          </div>
        </div>
      </div>

      <pet-student-list-dialog
        :visible.sync="drill.visible"
        :title="drill.title"
        :school-id="schoolId"
        :filters="drill.filters"
        :tags="drill.tags"
        :semesters="semesters" />
    </section>
  </section>
</template>

<script>
import DepartmentPane from '@/components/DepartmentPane.vue'
import PetStudentListDialog from './components/PetStudentListDialog.vue'
import { getPetStatistics, getPetSemesterList } from '@/api/modules/pet'
import { resolveActiveSchoolId, treeNodeDeptId, deptIdsEqual } from '@/utils/deptCascaderProps'

/** 日期格式化为 yyyy-MM-dd */
function formatDate (date) {
  const d = new Date(date)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default {
  name: 'PetStatistics',
  components: { DepartmentPane, PetStudentListDialog },
  data () {
    return {
      schoolId: 0,
      departmentId: 0,
      departmentName: '',
      loading: false,
      semesters: [],
      query: { semesterId: null, viewDate: formatDate(new Date()) },
      stat: {},
      classPage: 1,
      classPageSize: 10,
      classPageSizes: [10, 20, 50],
      drill: { visible: false, title: '', filters: {}, tags: [] }
    }
  },
  computed: {
    isToday () {
      return this.query.viewDate === formatDate(new Date())
    },
    dayText () {
      return this.isToday ? '今日' : '当日'
    },
    currentSemester () {
      return this.semesters.find(x => x.semesterId === this.query.semesterId) || null
    },
    datePickerOptions () {
      const semester = this.currentSemester
      const today = formatDate(new Date())
      return {
        disabledDate: date => {
          const value = formatDate(date)
          if (value > today) return true
          if (!semester) return false
          return value < String(semester.startDate || '').slice(0, 10) || value > String(semester.endDate || '').slice(0, 10)
        }
      }
    },
    claimRate () {
      return this.percent(this.stat.claimedCount, this.stat.totalStudentCount)
    },
    levelList () {
      return this.stat.levelDistribution || []
    },
    maxLevelCount () {
      return Math.max(1, ...this.levelList.map(x => x.count))
    },
    moodList () {
      return this.stat.moodDistribution || []
    },
    petList () {
      return this.stat.petDistribution || []
    },
    maxPetCount () {
      return Math.max(1, ...this.petList.map(x => x.count))
    },
    classList () {
      return (this.stat.classStats || []).map(x => Object.assign({}, x, { claimRate: this.percent(x.claimedCount, x.studentCount) }))
    },
    pagedClassList () {
      const start = (this.classPage - 1) * this.classPageSize
      return this.classList.slice(start, start + this.classPageSize)
    }
  },
  methods: {
    async treeNodeClick (data) {
      this.departmentId = treeNodeDeptId(data)
      this.departmentName = (data && (data.label || data.Name)) || ''
      const schoolId = resolveActiveSchoolId(this)
      if (schoolId && !deptIdsEqual(schoolId, this.schoolId)) {
        this.schoolId = schoolId
        await this.loadSemesters()
      }
      this.load()
    },
    async loadSemesters () {
      if (!this.schoolId) return
      const res = await getPetSemesterList({ schoolId: this.schoolId })
      this.semesters = (res.data && res.data.response) || []
      const current = this.semesters.find(x => x.isCurrent) || this.semesters[0]
      this.query.semesterId = current ? current.semesterId : null
      this.clampViewDate()
    },
    clampViewDate () {
      const semester = this.currentSemester
      const today = formatDate(new Date())
      if (!semester) {
        this.query.viewDate = today
        return
      }
      const start = String(semester.startDate || '').slice(0, 10)
      const end = String(semester.endDate || '').slice(0, 10)
      const latest = end && end < today ? end : today
      const value = this.query.viewDate
      if (!value || value > latest || (start && value < start)) this.query.viewDate = latest
    },
    handleSemesterChange () {
      this.clampViewDate()
      this.load()
    },
    async load () {
      if (!this.schoolId || !this.departmentId) return
      this.loading = true
      try {
        const res = await getPetStatistics({
          schoolId: this.schoolId,
          departmentId: this.departmentId,
          semesterId: this.query.semesterId || null,
          viewDate: this.query.viewDate || null
        })
        if (res.data && res.data.success) {
          this.stat = res.data.response || {}
          this.classPage = 1
        } else {
          this.$message.error((res.data && res.data.msg) || '统计失败')
        }
      } finally {
        this.loading = false
      }
    },
    /**
     * 打开学生明细弹窗，统一带上当前统计范围、学期和日期
     * @param {String} title - 弹窗标题
     * @param {Object} extra - 额外筛选条件
     * @param {Array} tags - 额外筛选说明
     * @param {Object} scope - 覆盖统计范围 { departmentId, departmentName }
     */
    openStudents (title, extra, tags = [], scope = null) {
      if (!this.schoolId || !this.departmentId) return
      const departmentId = scope ? scope.departmentId : this.departmentId
      const departmentName = scope ? scope.departmentName : this.departmentName
      this.drill = {
        visible: true,
        title,
        filters: Object.assign({
          departmentId,
          semesterId: this.query.semesterId || null,
          viewDate: this.query.viewDate || null
        }, extra),
        tags: [departmentName, this.stat.semesterName, `截至 ${this.query.viewDate}`, ...tags].filter(Boolean)
      }
    },
    openMood (m) {
      const moodCode = { Stable: 0, Energetic: 1, Adjusting: 2 }[m.mood]
      if (moodCode === undefined) return
      this.openStudents(`${this.dayText}${m.moodText}学生`, { moodCode }, [`${this.dayText}${m.moodText}`])
    },
    openClass (row) {
      this.openStudents(`${row.departmentName} 学生`, {}, [], { departmentId: row.departmentId, departmentName: row.departmentName })
    },
    percent (value, total) {
      return total ? Math.round((Number(value) || 0) / total * 100) : 0
    },
    barWidth (value, max) {
      if (!value || !max) return '0%'
      return Math.max(2, (value / max) * 100) + '%'
    }
  }
}
</script>

<style scoped>
.pet-stat-page { display: flex; flex-direction: column; min-height: 0; }
.page-head { flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 10px; }
.page-head .dp-dept-hint { margin: 0; }
.filter-bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.filter-bar__semester { width: 190px; }
.filter-bar__date { width: 140px !important; }
.page-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; overflow-x: hidden; display: flex; flex-direction: column; gap: 14px; padding-right: 4px; }
.page-body > * { flex-shrink: 0; }

.stat-strip { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.stat-card { display: flex; flex-direction: column; gap: 4px; padding: 12px 16px; background: #fff; border: 1px solid #e2e8f0; border-left: 3px solid #2563eb; border-radius: 10px; }
.stat-card--success { border-left-color: #16a34a; }
.stat-card--violet { border-left-color: #7c3aed; }
.stat-card--warning { border-left-color: #f59e0b; }
.stat-card__label { font-size: 12px; color: #64748b; }
.stat-card__value { font-size: 22px; font-weight: 600; color: #0f172a; line-height: 1.2; font-variant-numeric: tabular-nums; }
.stat-card__value small { font-size: 12px; font-weight: 400; color: #64748b; margin-left: 3px; }

.panel-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px; align-items: start; }
.panel { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; min-width: 0; }
.panel__head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; padding-bottom: 10px; margin-bottom: 12px; border-bottom: 1px solid #f1f5f9; }
.panel__head--sub { margin-top: 18px; }
.panel__title { font-size: 15px; font-weight: 600; color: #0f172a; }
.panel__desc { font-size: 12px; color: #64748b; }
.panel-empty { padding: 28px 0; text-align: center; color: #94a3b8; font-size: 13px; }

.bar-list { display: flex; flex-direction: column; gap: 10px; }
.bar-row { display: grid; grid-template-columns: 150px minmax(0, 1fr) 70px; align-items: center; gap: 10px; }
.bar-row__label { display: flex; align-items: center; gap: 6px; min-width: 0; }
.bar-row__sub { font-size: 12px; color: #475569; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bar-row__sub.strong { font-size: 13px; color: #0f172a; font-weight: 500; }
.bar-row__value { text-align: right; font-size: 13px; font-weight: 600; color: #0f172a; font-variant-numeric: tabular-nums; }
.bar-row__value small { font-weight: 400; color: #64748b; }
.bar-track { position: relative; height: 10px; border-radius: 999px; background: #f1f5f9; overflow: hidden; }
.bar-track--thin { height: 6px; flex: 1; }
.bar-fill { position: absolute; left: 0; top: 0; height: 100%; border-radius: 999px; background: #2563eb; transition: width .3s ease; }
.bar-fill--soft { background: #bfdbfe; }
.bar-fill--amber { background: #f59e0b; }
.bar-fill--green { background: #16a34a; }
.level-badge { flex-shrink: 0; display: inline-block; padding: 1px 7px; border-radius: 999px; background: #eff6ff; color: #1d4ed8; font-size: 12px; font-weight: 600; }
.off-tag { flex-shrink: 0; padding: 0 6px; border-radius: 999px; background: #f1f5f9; color: #64748b; font-size: 12px; }

.mood-stack { display: flex; height: 14px; border-radius: 999px; overflow: hidden; background: #f1f5f9; margin-bottom: 14px; }
.mood-stack__seg { height: 100%; transition: width .3s ease; }
.mood-energetic { background: #16a34a; }
.mood-stable { background: #3b82f6; }
.mood-adjusting { background: #f59e0b; }
.mood-legend { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.mood-legend__item { display: flex; flex-direction: column; gap: 2px; padding: 10px 12px; border: 1px solid #f1f5f9; border-radius: 8px; }
.mood-legend__item i { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.mood-legend__text { font-size: 12px; color: #475569; }
.mood-legend__value { font-size: 18px; font-weight: 600; color: #0f172a; font-variant-numeric: tabular-nums; }
.mood-legend__value small { font-size: 12px; font-weight: 400; color: #64748b; margin-left: 2px; }

.class-table { width: 100%; }
.class-table /deep/ .el-table__header-wrapper table,
.class-table /deep/ .el-table__body-wrapper table { table-layout: fixed; }
.class-table /deep/ .el-table__row:hover > td { background: #f5f9ff !important; }
.class-pager { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-top: 12px; padding-top: 12px; border-top: 1px dashed #e2e8f0; }
.class-pager__hint { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #94a3b8; }
.class-pager__hint i { color: #cbd5e1; }
.class-pager /deep/ .el-pagination { margin: 0 0 0 auto !important; gap: 8px !important; }
.is-clickable { cursor: pointer; }
.stat-card.is-clickable { transition: box-shadow .2s ease, transform .2s ease; }
.stat-card.is-clickable:hover { box-shadow: 0 4px 12px rgba(15, 23, 42, .08); transform: translateY(-1px); }
.stat-card__label i { margin-left: 4px; font-size: 11px; color: #cbd5e1; transition: color .2s ease; }
.stat-card.is-clickable:hover .stat-card__label i { color: #2563eb; }
.bar-row.is-clickable { margin: 0 -8px; padding: 4px 8px; border-radius: 6px; transition: background-color .2s ease; }
.bar-row.is-clickable:hover { background: #f5f9ff; }
.mood-legend__item.is-clickable { transition: border-color .2s ease, background-color .2s ease; }
.mood-legend__item.is-clickable:hover { border-color: #bfdbfe; background: #f8fbff; }
.mood-stack__seg { cursor: pointer; }
.class-table /deep/ .el-table__row.is-clickable { cursor: pointer; }
.rate-cell { display: flex; align-items: center; gap: 10px; }
.rate-cell__text { flex-shrink: 0; min-width: 96px; font-size: 12px; color: #475569; font-variant-numeric: tabular-nums; }
.mood-num { font-weight: 600; font-variant-numeric: tabular-nums; }
.mood-energetic-text { color: #15803d; }
.mood-adjusting-text { color: #b45309; }

@media (max-width: 1280px) {
  .panel-grid { grid-template-columns: 1fr; }
  .stat-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
